// Phase 5Z.9 — Fahrer-Login page
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { fahrerLogin } from "./lib/api";
import { useFahrerAuth } from "./lib/auth";
import { SUPPORTED_LANGUAGES } from "./i18n";

export default function FahrerLogin() {
  const { me, refresh } = useFahrerAuth();
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const nav = useNavigate();

  if (me === null) return null;
  if (me) return <Navigate to="/fahrer" replace />;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError(t("auth.no_credentials"));
      return;
    }
    setLoading(true);
    try {
      const resp = await fahrerLogin(username.trim().toLowerCase(), password);
      if (resp.preferred_language && resp.preferred_language !== i18n.resolvedLanguage) {
        i18n.changeLanguage(resp.preferred_language);
      }
      await refresh();
      if (resp.must_change_password) {
        nav("/fahrer/change-password", { replace: true });
      } else {
        nav("/fahrer", { replace: true });
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) setError(t("auth.locked"));
      else setError(e.response?.data?.detail || t("auth.login_failed"));
    } finally {
      setLoading(false);
    }
  }

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.resolvedLanguage) || SUPPORTED_LANGUAGES[0];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="fahrer-login-page">
      {/* Lang switcher (top right) */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={() => setShowLang(v => !v)}
          data-testid="fahrer-login-lang-toggle"
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-sm flex items-center gap-2 hover:border-amber-600"
        >
          <span>{currentLang.flag}</span>
          <span className="text-xs text-zinc-300">{currentLang.label}</span>
        </button>
        {showLang && (
          <div className="absolute top-12 right-0 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl py-1 min-w-[160px]">
            {SUPPORTED_LANGUAGES.map(l => (
              <button
                key={l.code}
                onClick={() => { i18n.changeLanguage(l.code); setShowLang(false); }}
                data-testid={`fahrer-login-lang-${l.code}`}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-800 flex items-center gap-2 ${i18n.resolvedLanguage === l.code ? "text-amber-400 font-medium" : "text-zinc-200"}`}
              >
                <span>{l.flag}</span>
                {l.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-600/20">
            <span className="text-3xl font-bold text-white">M</span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-50">{t("app.name")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("app.tagline")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="fahrer-login-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("auth.username")}</label>
            <input
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="fahrer-login-username"
              placeholder={t("auth.username_placeholder")}
              className="w-full mt-1.5 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-base text-zinc-100 focus:border-amber-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("auth.password")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="fahrer-login-password"
              placeholder={t("auth.password_placeholder")}
              className="w-full mt-1.5 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-base text-zinc-100 focus:border-amber-600 focus:outline-none"
            />
          </div>
          {error && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300" data-testid="fahrer-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="fahrer-login-submit"
            className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white font-semibold rounded-lg transition-colors"
          >
            {loading ? t("app.loading") : t("auth.submit")}
          </button>
        </form>

        <div className="mt-8 text-center text-[10px] text-zinc-700 italic tracking-wider" data-testid="fahrer-login-developed-by">
          Munja Trans GmbH · Entwicklung bei <span className="text-amber-500/70 not-italic font-semibold">Ichkov</span>
        </div>
      </div>
    </div>
  );
}
